import { getSession } from 'next-auth/react';
import type { Dispatch } from 'react';

import { addAuthorizationHeader } from '@/lib/functions/addAuthorizationHeader';
import { getOrderToken } from '@/lib/functions/getOrderToken';
import { logMessage } from '@/lib/functions/logMessage';

import type { OrderActions } from '@/lib/context/orderActions';
import type { OrderState as OrderStateType } from '@/lib/types/orderState';

interface SetOrderDetailsPayload {
  notes?: string;
  isGift?: boolean;
  giftMessage?: string;
  allowSubstitutions?: boolean;
  tipAmount?: number;
}

export async function setOrderDetails(
  tokenValue: string | null | undefined,
  orderState: OrderStateType,
  orderDispatch: Dispatch<OrderActions>,
  payload: SetOrderDetailsPayload,
  retries = 0,
  forceRefreshToken = false,
) {
  try {
    let effectiveToken = tokenValue;

    if (!effectiveToken || forceRefreshToken) {
      const tokenResp = await getOrderToken(orderState, orderDispatch, forceRefreshToken);
      if (tokenResp.okay && tokenResp.token) {
        effectiveToken = tokenResp.token;
      }
    }

    if (!effectiveToken) {
      return { success: false, message: 'Order token is required' };
    }

    const session = await getSession();
    const headers = addAuthorizationHeader(
      { 'Content-Type': 'application/json' },
      orderState?.tokenType === 'customer' ? session?.token : null,
    );

    const body = JSON.stringify(payload);

    const response = await fetch(`/api/orders/${effectiveToken}/details`, {
      method: 'POST',
      cache: 'no-cache',
      headers,
      body,
    });

    switch (response?.status) {
      case 200: {
        const data = await response.json();
        return { success: true, message: '', orderData: data };
      }
      case 400: {
        logMessage('Exception in POST set order details', 'error');
        const data = await response.json();
        return { success: false, message: data.error ?? '' };
      }
      case 404: {
        if (retries < 1) {
          return await setOrderDetails('', orderState, orderDispatch, payload, retries + 1, true);
        }
        logMessage('Exception in POST set order details (404)', 'error');
        return { success: false, message: 'Order not found' };
      }
      default: {
        logMessage('Exception in POST set order details (default catch)', 'error');
        return { success: false, message: 'Unknown error' };
      }
    }
  } catch {
    logMessage('Exception in POST set order details', 'error');
    return { success: false, message: '' };
  }
}
